import { base64URLToBytes, bytesToBase64URL } from '../util/base64';

import { randomBytes, secretbox } from 'tweetnacl';

export const PASSKEY_AUK_ENVELOPE_ALGORITHM = 'hkdf-sha256-secretbox-v1';

export interface PasskeyAukEnvelope {
  version: number;
  aukVersion: number;
  algorithm: string;
  encAuk: string;
  encAukNonce: string;
  rpId: string;
}

export async function encryptAukForPasskeyPRF(
  auk: Uint8Array,
  prfOutput: Uint8Array,
  rpId: string,
  credentialID: string,
  aukVersion = 1
): Promise<Omit<PasskeyAukEnvelope, 'rpId'>> {
  if (auk.length !== 32) {
    throw new Error('passkey vault AUK must be 32 bytes');
  }
  const wrappingKey = await derivePasskeyAukWrappingKey(prfOutput, rpId, credentialID, aukVersion);
  const nonce = randomBytes(secretbox.nonceLength);
  const encAuk = secretbox(auk, nonce, wrappingKey);
  return {
    version: 1,
    aukVersion,
    algorithm: PASSKEY_AUK_ENVELOPE_ALGORITHM,
    encAuk: bytesToBase64URL(encAuk),
    encAukNonce: bytesToBase64URL(nonce),
  };
}

export async function decryptAukForPasskeyPRF(
  envelope: PasskeyAukEnvelope,
  prfOutput: Uint8Array,
  credentialID: string
): Promise<Uint8Array> {
  if (envelope.version !== 1 || envelope.algorithm !== PASSKEY_AUK_ENVELOPE_ALGORITHM) {
    throw new Error('unsupported passkey vault envelope');
  }
  const encAuk = base64URLToBytes(envelope.encAuk);
  const nonce = base64URLToBytes(envelope.encAukNonce);
  if (encAuk.length !== 48 || nonce.length !== secretbox.nonceLength) {
    throw new Error('invalid passkey vault envelope');
  }
  const wrappingKey = await derivePasskeyAukWrappingKey(
    prfOutput,
    envelope.rpId,
    credentialID,
    envelope.aukVersion
  );
  const auk = secretbox.open(encAuk, nonce, wrappingKey);
  if (!auk) {
    throw new Error('failed to unlock vault with passkey');
  }
  return auk;
}

async function derivePasskeyAukWrappingKey(
  prfOutput: Uint8Array,
  rpId: string,
  credentialID: string,
  aukVersion: number
): Promise<Uint8Array> {
  if (!rpId || !credentialID || aukVersion < 1) {
    throw new Error('invalid passkey vault envelope context');
  }
  const key = await globalThis.crypto.subtle.importKey(
    'raw',
    prfOutput as BufferSource,
    'HKDF',
    false,
    ['deriveBits']
  );
  const context = `clovyr.passkey-auk/v1:${rpId}:${credentialID}:${aukVersion}`;
  const bits = await globalThis.crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(32),
      info: new TextEncoder().encode(context),
    },
    key,
    256
  );
  return new Uint8Array(bits);
}
